import {
	getComponentMetadataResolved,
	getComponentMetadataRejected,
	getLoFComponentAssetMetadata,
	getLoFComponentInspectionMetadata,
	getCoFComponentAssetMetadata,
	getLoFComponentProximityMetadata,
	getCoFComponentProximityMetadata,
	getLoFComponentProximityAttrMetadata,
	getCoFComponentProximityAttrMetadata,
	getLoFComponentCustomTableMetadata,
	getCoFComponentCustomTableMetadata,
	getLoFComponentTaskMetadata,
	getCoFComponentTaskMetadata,
	getLoFComponentFailureDefinitionMetadata,
	getCoFComponentFailureDefinitionMetadata,
	getLoFComponentDeteriorationModelMetadata,
	getCoFComponentDeteriorationModelMetadata,
} from '@Actions';
import { call, put, takeLatest } from '@redux-saga/core/effects';
import {
	getLoFComponentAssetMetadataApi,
	getLoFComponentInspectionMetadataApi,
	getLoFComponentCustomTableMetadataApi,
	getLoFComponentProximityAttrMetadataApi,
	getLoFComponentTaskMetadataApi,
	getLoFComponentFailureDefinitionMetadataApi,
	getLoFComponentDeteriorationModelMetadataApi,
} from '@Services/LoF/LoF.services';
import {
	getCoFComponentCustomTableMetadataApi,
	getCoFComponentAssetMetadataApi,
	getCoFComponentProximityAttrMetadataApi,
	getCoFComponentProximityMetadataApi,
	getCoFComponentTaskMetadataApi,
	getCoFComponentFailureDefinitionMetadataApi,
	getCoFComponentDeteriorationModelMetadataApi,
} from '@Services/CoF/CoF.services';
import {
	ComponentMetadataResponse,
	GetComponentMetadataParams,
} from '@Types/component.types';
import { AxiosResponse } from 'axios';
import { retry } from 'redux-saga/effects';
import { AnyAction } from 'redux';
import { error } from '@innovyze/stylovyze';
import { t } from 'i18next';
import {
	defaultMultiParamUniqueLimit,
	singleParamUniqueLimit,
} from '@Utils/uniqueValueLimits';

// Drops the unique list when it exceeds the relevant limit, telling the user
// how many values the field has against that limit; an empty list is dropped too.
export function* applyUniqueLimit(
	data: ComponentMetadataResponse,
	payload: GetComponentMetadataParams,
) {
	if (!data.unique) return;
	const total = data.unique.length;
	const limit = payload.multiParam
		? payload.multiParamUniqueLimit ?? defaultMultiParamUniqueLimit
		: singleParamUniqueLimit;
	if (total > limit) {
		const options = { field: payload.fieldAlias, total, limit };
		yield put(
			error(
				payload.multiParam
					? t(
							'{{field}} has {{total}} unique values, which exceeds the multi-parameter limit of {{limit}}',
							options,
					  )
					: t(
							'{{field}} has {{total}} unique values, which exceeds the limit of {{limit}}',
							options,
					  ),
			),
		);
		delete data.unique;
	} else if (total === 0) delete data.unique;
}

export function* getLoFComponentInspectionMetadataSaga(action: AnyAction) {
	try {
		const response: AxiosResponse<ComponentMetadataResponse> = yield retry(
			5,
			1500,
			getLoFComponentInspectionMetadataApi,
			action.payload.assetType,
			action.payload.field,
		);
		yield call(applyUniqueLimit, response.data, action.payload);
		yield put(getComponentMetadataResolved(response.data));
	} catch (e) {
		yield put(
			error(
				t('Scoring cannot be set due insufficient data for {{field}}', {
					field: action.payload.fieldAlias,
				}),
			),
		);
		yield put(getComponentMetadataRejected());
	}
}

export function* getLoFComponentAssetMetadataSaga(action: AnyAction) {
	try {
		const response: AxiosResponse<ComponentMetadataResponse> = yield retry(
			5,
			1500,
			getLoFComponentAssetMetadataApi,
			action.payload.assetType,
			action.payload.field,
		);
		yield call(applyUniqueLimit, response.data, action.payload);
		yield put(getComponentMetadataResolved(response.data));
	} catch (e) {
		yield put(
			error(
				t('Scoring cannot be set due insufficient data for {{field}}', {
					field: action.payload.fieldAlias,
				}),
			),
		);
		yield put(getComponentMetadataRejected());
	}
}

export function* getCoFComponentAssetMetadataSaga(action: AnyAction) {
	try {
		const response: AxiosResponse<ComponentMetadataResponse> = yield retry(
			5,
			1500,
			getCoFComponentAssetMetadataApi,
			action.payload.assetType,
			action.payload.field,
		);
		yield call(applyUniqueLimit, response.data, action.payload);
		yield put(getComponentMetadataResolved(response.data));
	} catch (e) {
		yield put(
			error(
				t('Scoring cannot be set due insufficient data for {{field}}', {
					field: action.payload.fieldAlias,
				}),
			),
		);
		yield put(getComponentMetadataRejected());
	}
}

export function* getLoFComponentProximityMetadataSaga(action: AnyAction) {
	try {
		const response: AxiosResponse<ComponentMetadataResponse> = yield retry(
			5,
			1500,
			getCoFComponentProximityMetadataApi,
			action.payload,
		);
		yield call(applyUniqueLimit, response.data, action.payload);
		yield put(getComponentMetadataResolved(response.data));
	} catch (e) {
		yield put(getComponentMetadataRejected());
	}
}

export function* getCoFComponentProximityMetadataSaga(action: AnyAction) {
	try {
		const response: AxiosResponse<ComponentMetadataResponse> = yield retry(
			5,
			1500,
			getCoFComponentProximityMetadataApi,
			action.payload,
		);
		yield call(applyUniqueLimit, response.data, action.payload);
		yield put(getComponentMetadataResolved(response.data));
	} catch (e) {
		yield put(getComponentMetadataRejected());
	}
}

export function* getLoFComponentProximityAttrMetadataSaga(action: AnyAction) {
	try {
		const response: AxiosResponse<ComponentMetadataResponse> = yield retry(
			5,
			1500,
			getLoFComponentProximityAttrMetadataApi,
			action.payload.assetType,
			action.payload.field,
			action.payload.layerName,
		);
		yield call(applyUniqueLimit, response.data, action.payload);
		yield put(getComponentMetadataResolved(response.data));
	} catch {
		yield put(
			error(
				t('Scoring cannot be set due insufficient data for {{field}}', {
					field: action.payload.fieldAlias,
				}),
			),
		);
		yield put(getComponentMetadataRejected());
	}
}

export function* getCoFComponentProximityAttrMetadataSaga(action: AnyAction) {
	try {
		const response: AxiosResponse<ComponentMetadataResponse> = yield retry(
			5,
			1500,
			getCoFComponentProximityAttrMetadataApi,
			action.payload.assetType,
			action.payload.field,
			action.payload.layerName,
		);
		yield call(applyUniqueLimit, response.data, action.payload);
		yield put(getComponentMetadataResolved(response.data));
	} catch {
		yield put(
			error(
				t('Scoring cannot be set due insufficient data for {{field}}', {
					field: action.payload.fieldAlias,
				}),
			),
		);
		yield put(getComponentMetadataRejected());
	}
}

export function* getLoFComponentCustomTableMetadataSaga(action: AnyAction) {
	try {
		const response: AxiosResponse<ComponentMetadataResponse> = yield retry(
			5,
			1500,
			getLoFComponentCustomTableMetadataApi,
			action.payload.assetType,
			action.payload.field,
			action.payload.layerName,
		);
		yield call(applyUniqueLimit, response.data, action.payload);
		yield put(getComponentMetadataResolved(response.data));
	} catch (e) {
		yield put(getComponentMetadataRejected());
	}
}

export function* getCoFComponentCustomTableMetadataSaga(action: AnyAction) {
	try {
		const response: AxiosResponse<ComponentMetadataResponse> = yield retry(
			5,
			1500,
			getCoFComponentCustomTableMetadataApi,
			action.payload.assetType,
			action.payload.field,
			action.payload.layerName,
		);
		yield call(applyUniqueLimit, response.data, action.payload);
		yield put(getComponentMetadataResolved(response.data));
	} catch (e) {
		yield put(getComponentMetadataRejected());
	}
}

export function* getLoFComponentTaskMetadataSaga(action: AnyAction) {
	try {
		const response: AxiosResponse<ComponentMetadataResponse> = yield retry(
			5,
			1500,
			getLoFComponentTaskMetadataApi,
			action.payload.assetType,
			action.payload.field,
			action.payload.layerName,
		);
		yield call(applyUniqueLimit, response.data, action.payload);
		yield put(getComponentMetadataResolved(response.data));
	} catch {
		yield put(
			error(
				t('Scoring cannot be set due insufficient data for {{field}}', {
					field: action.payload.fieldAlias,
				}),
			),
		);
		yield put(getComponentMetadataRejected());
	}
}

export function* getCoFComponentTaskMetadataSaga(action: AnyAction) {
	try {
		const response: AxiosResponse<ComponentMetadataResponse> = yield retry(
			5,
			1500,
			getCoFComponentTaskMetadataApi,
			action.payload.assetType,
			action.payload.field,
			action.payload.layerName,
		);
		yield call(applyUniqueLimit, response.data, action.payload);
		yield put(getComponentMetadataResolved(response.data));
	} catch {
		yield put(
			error(
				t('Scoring cannot be set due insufficient data for {{field}}', {
					field: action.payload.fieldAlias,
				}),
			),
		);
		yield put(getComponentMetadataRejected());
	}
}

export function* getLoFComponentFailureDefinitionMetadataSaga(
	action: AnyAction,
) {
	try {
		const response: AxiosResponse<ComponentMetadataResponse> = yield retry(
			5,
			1500,
			getLoFComponentFailureDefinitionMetadataApi,
			action.payload.assetType,
			action.payload.field,
			action.payload.failureDefinitionId,
		);
		yield call(applyUniqueLimit, response.data, action.payload);
		yield put(getComponentMetadataResolved(response.data));
	} catch (e) {
		yield put(
			error(
				t('Scoring cannot be set due insufficient data for {{field}}', {
					field: action.payload.fieldAlias,
				}),
			),
		);
		yield put(getComponentMetadataRejected());
	}
}

export function* getCoFComponentFailureDefinitionMetadataSaga(
	action: AnyAction,
) {
	try {
		const response: AxiosResponse<ComponentMetadataResponse> = yield retry(
			5,
			1500,
			getCoFComponentFailureDefinitionMetadataApi,
			action.payload.assetType,
			action.payload.field,
			action.payload.failureDefinitionId,
		);
		yield call(applyUniqueLimit, response.data, action.payload);
		yield put(getComponentMetadataResolved(response.data));
	} catch (e) {
		yield put(
			error(
				t('Scoring cannot be set due insufficient data for {{field}}', {
					field: action.payload.fieldAlias,
				}),
			),
		);
		yield put(getComponentMetadataRejected());
	}
}

export function* getLoFComponentDeteriorationModelMetadataSaga(
	action: AnyAction,
) {
	try {
		const response: AxiosResponse<ComponentMetadataResponse> = yield retry(
			5,
			1500,
			getLoFComponentDeteriorationModelMetadataApi,
			action.payload.assetType,
			action.payload.field,
			action.payload.deteriorationModelId,
		);
		yield call(applyUniqueLimit, response.data, action.payload);
		yield put(getComponentMetadataResolved(response.data));
	} catch (e) {
		yield put(
			error(
				t('Scoring cannot be set due insufficient data for {{field}}', {
					field: action.payload.fieldAlias,
				}),
			),
		);
		yield put(getComponentMetadataRejected());
	}
}

export function* getCoFComponentDeteriorationModelMetadataSaga(
	action: AnyAction,
) {
	try {
		const response: AxiosResponse<ComponentMetadataResponse> = yield retry(
			5,
			1500,
			getCoFComponentDeteriorationModelMetadataApi,
			action.payload.assetType,
			action.payload.field,
			action.payload.deteriorationModelId,
		);
		yield call(applyUniqueLimit, response.data, action.payload);
		yield put(getComponentMetadataResolved(response.data));
	} catch (e) {
		yield put(
			error(
				t('Scoring cannot be set due insufficient data for {{field}}', {
					field: action.payload.fieldAlias,
				}),
			),
		);
		yield put(getComponentMetadataRejected());
	}
}

function* watchGetComponentMetadata() {
	yield takeLatest(
		getLoFComponentInspectionMetadata,
		getLoFComponentInspectionMetadataSaga,
	);
	yield takeLatest(
		getLoFComponentAssetMetadata,
		getLoFComponentAssetMetadataSaga,
	);
	yield takeLatest(
		getCoFComponentAssetMetadata,
		getCoFComponentAssetMetadataSaga,
	);
	yield takeLatest(
		getLoFComponentProximityMetadata,
		getLoFComponentProximityMetadataSaga,
	);
	yield takeLatest(
		getCoFComponentProximityMetadata,
		getCoFComponentProximityMetadataSaga,
	);
	yield takeLatest(
		getLoFComponentProximityAttrMetadata,
		getLoFComponentProximityAttrMetadataSaga,
	);
	yield takeLatest(
		getCoFComponentProximityAttrMetadata,
		getCoFComponentProximityAttrMetadataSaga,
	);
	yield takeLatest(
		getLoFComponentCustomTableMetadata,
		getLoFComponentCustomTableMetadataSaga,
	);
	yield takeLatest(
		getCoFComponentCustomTableMetadata,
		getCoFComponentCustomTableMetadataSaga,
	);
	yield takeLatest(
		getLoFComponentTaskMetadata,
		getLoFComponentTaskMetadataSaga,
	);
	yield takeLatest(
		getCoFComponentTaskMetadata,
		getCoFComponentTaskMetadataSaga,
	);
	yield takeLatest(
		getLoFComponentFailureDefinitionMetadata,
		getLoFComponentFailureDefinitionMetadataSaga,
	);
	yield takeLatest(
		getCoFComponentFailureDefinitionMetadata,
		getCoFComponentFailureDefinitionMetadataSaga,
	);
	yield takeLatest(
		getLoFComponentDeteriorationModelMetadata,
		getLoFComponentDeteriorationModelMetadataSaga,
	);
	yield takeLatest(
		getCoFComponentDeteriorationModelMetadata,
		getCoFComponentDeteriorationModelMetadataSaga,
	);
}

const sagaArray = [watchGetComponentMetadata()];

export default sagaArray;
